export const DURATION_FRAMES = 1492;

import React from "react";
import { AbsoluteFill, Audio, Sequence, staticFile, useCurrentFrame, interpolate, spring, useVideoConfig } from "remotion";
import { noise2D } from "@remotion/noise";
import { fonts, dark } from "../styles/theme";

/**
 * Reel_cmohmvwuo00043pqvdfg53pmb — clean-light template
 * Total: 1492 frames @ 30fps (49.7s)
 * Audio: cmohmvwuo00043pqvdfg53pmb.mp3 from frame 5
 *
 * Scene 1 "Hook":             f0    -f104  (104f)  voice 0.000s-3.122s
 * Scene 2 "Puente visual":    f104  -f236  (132f)  voice 3.442s-7.682s
 * Scene 3 "Dato frío":        f236  -f343  (107f)  voice 7.842s-10.564s
 * Scene 4 "Gráfico barras":   f343  -f827  (484f)  voice 11.416s-17.362s
 * Scene 5 "Reframe":          f827  -f1059 (232f)  voice 27.522s-35.201s
 * Scene 6 "Solución":         f1059 -f1241 (182f)  voice 35.261s-41.279s
 * Scene 7 "Cierre conceptual":f1241 -f1351 (110f)  voice 41.336s-44.719s
 * Scene 8 "CTA":              f1351 -f1492 (141f)  voice 44.999s-46.556s
 */

const SPRING = { damping: 20, stiffness: 80, mass: 1 };
const KEYWORD_GRADIENT = "linear-gradient(135deg, #60a5fa, #2563eb, #818cf8)";
const BLUE_SOLID = "#004ac6";
const TEXT_PRIMARY = "#191c1e";
const TEXT_SECONDARY = "#434655";
const BG_BASE = "#f7f9fb";
const BG_ALT = "#f0f2f5";
const CARD_SHADOW = "0 20px 40px rgba(0,0,0,0.06)";

// ─────────────────────────────────────────────
// CONTINUOUS BACKGROUND
// ─────────────────────────────────────────────
const ContinuousBackground: React.FC = () => {
  const frame = useCurrentFrame();
  const { width, height } = useVideoConfig();

  const parallaxY = (frame * 0.4) % 40;
  const parallaxX = Math.sin(frame / 60) * 8;

  // Soft drifting blobs
  const blob1X = interpolate(frame, [0, 1492], [-200, 200]);
  const blob2X = interpolate(frame, [0, 1492], [200, -200]);
  const blob1Y = Math.sin(frame / 80) * 60;
  const blob2Y = Math.cos(frame / 90) * 60;

  // Section alternation between scenes
  const sectionTone = interpolate(
    frame,
    [0, 104, 236, 343, 827, 1059, 1241, 1351, 1492],
    [0, 1, 0, 1, 0, 1, 0, 1, 0],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
  );
  const altBg = sectionTone > 0.5 ? BG_ALT : "#ffffff";

  return (
    <AbsoluteFill
      style={{
        background: `linear-gradient(180deg, ${BG_BASE} 0%, ${altBg} 100%)`,
      }}
    >
      {/* Soft drifting accent blobs */}
      <div
        style={{
          position: "absolute",
          left: blob1X,
          top: 100 + blob1Y,
          width: 600,
          height: 600,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(0,74,198,0.06) 0%, rgba(0,74,198,0) 70%)",
          filter: "blur(20px)",
        }}
      />
      <div
        style={{
          position: "absolute",
          right: blob2X,
          bottom: 100 + blob2Y,
          width: 700,
          height: 700,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(37,99,235,0.05) 0%, rgba(37,99,235,0) 70%)",
          filter: "blur(20px)",
        }}
      />

      {/* Dot grid with parallax */}
      <div
        style={{
          position: "absolute",
          inset: -50,
          backgroundImage:
            "radial-gradient(circle, rgba(0,0,0,0.04) 1px, transparent 1px)",
          backgroundSize: "40px 40px",
          backgroundPosition: `${parallaxX}px ${parallaxY}px`,
        }}
      />

      {/* Subtle vignette */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(ellipse at center, transparent 50%, rgba(0,0,0,0.04) 100%)",
        }}
      />
    </AbsoluteFill>
  );
};

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────
const useEnter = (delay = 0) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const local = Math.max(0, frame - delay);
  const opacity = interpolate(local, [0, 25], [0, 1], {
    extrapolateRight: "clamp",
  });
  const translateY = interpolate(local, [0, 25], [60, 0], {
    extrapolateRight: "clamp",
  });
  const s = spring({ frame: local, fps, config: SPRING });
  return { opacity, translateY, spring: s };
};

const useExit = (start: number, duration = 18) => {
  const frame = useCurrentFrame();
  const local = Math.max(0, frame - start);
  const opacity = interpolate(local, [0, duration], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return opacity;
};

// ─────────────────────────────────────────────
// SCENE 1 — HOOK
// ─────────────────────────────────────────────
const Scene1Hook: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Glitch on entry (frames 0-12)
  const glitch = frame < 12 ? Math.sin(frame * 3) * (12 - frame) * 0.6 : 0;
  const glitchOpacity = frame < 12 ? 1 : 0;

  const line1 = useEnter(4);
  const line2 = useEnter(26);

  // Cursor blink
  const cursorOn = Math.floor(frame / 15) % 2 === 0;

  // Exit fade
  const exit = interpolate(frame, [86, 104], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // Scale pulse on container
  const containerScale = spring({ frame, fps, config: SPRING, from: 0.96, to: 1 });

  return (
    <AbsoluteFill
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        opacity: exit,
        padding: 60,
      }}
    >
      {/* Accent block */}
      <div
        style={{
          position: "absolute",
          left: "8%",
          top: "20%",
          width: 8,
          height: 80,
          background: BLUE_SOLID,
          opacity: line1.opacity * 0.8,
          transform: `scaleY(${line1.spring})`,
          transformOrigin: "top",
        }}
      />
      <div
        style={{
          position: "absolute",
          right: "8%",
          bottom: "20%",
          width: 8,
          height: 80,
          background: BLUE_SOLID,
          opacity: line2.opacity * 0.8,
          transform: `scaleY(${line2.spring})`,
          transformOrigin: "bottom",
        }}
      />

      <div
        style={{
          textAlign: "center",
          transform: `scale(${containerScale})`,
        }}
      >
        {/* Glitch ghost layer */}
        {glitchOpacity > 0 && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              transform: `translate(${glitch}px, ${-glitch * 0.5}px)`,
              opacity: glitchOpacity,
              color: "#ff3355",
              mixBlendMode: "multiply",
              filter: "blur(0.5px)",
            }}
          >
            <span
              style={{
                fontFamily: fonts.heading,
                fontWeight: 900,
                fontSize: 96,
                lineHeight: 1.05,
              }}
            >
              Tu competidor no es mejor.
            </span>
          </div>
        )}

        <div
          style={{
            opacity: line1.opacity,
            transform: `translateY(${line1.translateY}px)`,
            fontFamily: fonts.heading,
            fontWeight: 900,
            fontSize: 96,
            lineHeight: 1.05,
            color: TEXT_PRIMARY,
            letterSpacing: -2,
          }}
        >
          Tu competidor no es mejor.
        </div>
        <div
          style={{
            marginTop: 16,
            opacity: line2.opacity,
            transform: `translateY(${line2.translateY}px)`,
            fontFamily: fonts.heading,
            fontWeight: 900,
            fontSize: 96,
            lineHeight: 1.05,
            color: TEXT_PRIMARY,
            letterSpacing: -2,
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          Solo está
          <span
            style={{
              background: KEYWORD_GRADIENT,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            arriba.
          </span>
          <span
            style={{
              display: "inline-block",
              width: 8,
              height: 88,
              background: TEXT_PRIMARY,
              marginLeft: 6,
              opacity: cursorOn ? 1 : 0,
            }}
          />
        </div>
      </div>
    </AbsoluteFill>
  );
};

// ─────────────────────────────────────────────
// SCENE 2 — PUENTE VISUAL (search bar)
// ─────────────────────────────────────────────
const Scene2Bridge: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const bar = useEnter(4);
  const containerScale = spring({
    frame: frame - 4,
    fps,
    config: SPRING,
    from: 0.92,
    to: 1,
  });

  // Typewriter effect on "fontanero cerca de mí"
  const fullText = "fontanero cerca de mí";
  const typeStart = 32;
  const typeEnd = 110;
  const charCount = Math.max(
    0,
    Math.floor(
      interpolate(frame, [typeStart, typeEnd], [0, fullText.length], {
        extrapolateLeft: "clamp",
        extrapolateRight: "clamp",
      })
    )
  );
  const typedText = fullText.slice(0, charCount);
  const cursorOn = Math.floor(frame / 12) % 2 === 0;

  const exit = interpolate(frame, [114, 132], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  return (
    <AbsoluteFill
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        opacity: exit,
      }}
    >
      {/* Floating accent dots */}
      {[0, 1, 2, 3, 4].map((i) => {
        const off = Math.sin((frame + i * 30) / 30) * 12;
        return (
          <div
            key={i}
            style={{
              position: "absolute",
              left: `${15 + i * 18}%`,
              top: `${20 + (i % 2) * 60}%`,
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: BLUE_SOLID,
              opacity: 0.25,
              transform: `translateY(${off}px)`,
            }}
          />
        );
      })}

      <div
        style={{
          opacity: bar.opacity,
          transform: `translateY(${bar.translateY}px) scale(${containerScale})`,
          width: "75%",
          background: "#ffffff",
          borderRadius: 999,
          boxShadow: CARD_SHADOW,
          padding: "28px 40px",
          display: "flex",
          alignItems: "center",
          gap: 24,
          border: "1px solid rgba(0,0,0,0.05)",
        }}
      >
        {/* Magnifier icon */}
        <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
          <circle
            cx="19"
            cy="19"
            r="12"
            stroke={TEXT_SECONDARY}
            strokeWidth="3"
          />
          <line
            x1="29"
            y1="29"
            x2="38"
            y2="38"
            stroke={TEXT_SECONDARY}
            strokeWidth="3"
            strokeLinecap="round"
          />
        </svg>

        <div
          style={{
            fontFamily: fonts.body,
            fontSize: 48,
            color: TEXT_PRIMARY,
            fontWeight: 500,
            letterSpacing: -0.5,
            display: "flex",
            alignItems: "center",
            flex: 1,
          }}
        >
          {typedText}
          <span
            style={{
              display: "inline-block",
              width: 3,
              height: 50,
              background: TEXT_PRIMARY,
              marginLeft: 4,
              opacity: cursorOn ? 1 : 0,
            }}
          />
        </div>

        {/* Google-style colored dots */}
        <div style={{ display: "flex", gap: 8 }}>
          <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#4285F4" }} />
          <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#EA4335" }} />
          <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#FBBC05" }} />
          <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#34A853" }} />
        </div>
      </div>
    </AbsoluteFill>
  );
};

// ─────────────────────────────────────────────
// SCENE 3 — DATO FRÍO (search results list)
// ─────────────────────────────────────────────
const Scene3ColdFact: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const items = [0, 1, 2, 3];
  const highlightStart = 30;

  // Highlight #1 result
  const highlightProgress = spring({
    frame: frame - highlightStart,
    fps,
    config: SPRING,
  });

  // Percentage counter
  const pctStart = 50;
  const pctValue = Math.floor(
    interpolate(frame, [pctStart, pctStart + 35], [0, 27], {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
    })
  );

  const labelEnter = useEnter(70);

  const exit = interpolate(frame, [89, 107], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  return (
    <AbsoluteFill
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        opacity: exit,
        flexDirection: "column",
        gap: 24,
        padding: 80,
      }}
    >
      <div style={{ width: "70%", display: "flex", flexDirection: "column", gap: 16 }}>
        {items.map((i) => {
          const e = useEnter(i * 6);
          const isFirst = i === 0;
          const bg = isFirst
            ? `rgba(25, 28, 30, ${highlightProgress})`
            : "#e7eaee";
          const textColor = isFirst
            ? `rgba(255,255,255,${highlightProgress})`
            : "transparent";
          return (
            <div
              key={i}
              style={{
                opacity: e.opacity,
                transform: `translateY(${e.translateY}px)`,
                background: bg,
                borderRadius: 12,
                padding: "20px 28px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                boxShadow: isFirst ? `0 12px 28px rgba(0,0,0,${0.18 * highlightProgress})` : "none",
                transition: "all 0.2s",
                position: "relative",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 18, flex: 1 }}>
                <div
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    background: isFirst ? "#fff" : "#bfc4c9",
                    opacity: isFirst ? highlightProgress : 1,
                  }}
                />
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      height: 14,
                      width: "60%",
                      borderRadius: 4,
                      background: isFirst ? "rgba(255,255,255,0.85)" : "#cbd0d6",
                    }}
                  />
                  <div
                    style={{
                      height: 10,
                      width: "85%",
                      marginTop: 8,
                      borderRadius: 4,
                      background: isFirst ? "rgba(255,255,255,0.4)" : "#d8dce1",
                    }}
                  />
                </div>
              </div>
              {isFirst && (
                <div
                  style={{
                    fontFamily: fonts.heading,
                    fontWeight: 900,
                    fontSize: 56,
                    color: textColor,
                    minWidth: 140,
                    textAlign: "right",
                    letterSpacing: -1,
                  }}
                >
                  {pctValue}%
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div
        style={{
          opacity: labelEnter.opacity,
          transform: `translateY(${labelEnter.translateY}px)`,
          marginTop: 20,
          fontFamily: fonts.heading,
          fontWeight: 800,
          fontSize: 38,
          color: TEXT_PRIMARY,
          display: "flex",
          alignItems: "center",
          gap: 14,
        }}
      >
        Posición #1
        <span style={{ color: BLUE_SOLID }}>→</span>
        <span
          style={{
            background: KEYWORD_GRADIENT,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            fontWeight: 900,
          }}
        >
          27% de los clics
        </span>
      </div>
    </AbsoluteFill>
  );
};

// ─────────────────────────────────────────────
// SCENE 4 — GRÁFICO DE BARRAS
// ─────────────────────────────────────────────
const Scene4Bars: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const titleEnter = useEnter(4);

  const bars = [
    { label: "#1", pct: 27, color: BLUE_SOLID, height: 380 },
    { label: "#5", pct: 4, color: "#2563eb", height: 90 },
    { label: "#10", pct: 2, color: "#818cf8", height: 50 },
  ];

  // Domino fall animation
  const fallStart = 30;
  const exit = interpolate(frame, [466, 484], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  return (
    <AbsoluteFill
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        opacity: exit,
        padding: 80,
        gap: 40,
      }}
    >
      <div
        style={{
          opacity: titleEnter.opacity,
          transform: `translateY(${titleEnter.translateY}px)`,
          fontFamily: fonts.heading,
          fontWeight: 900,
          fontSize: 56,
          color: TEXT_PRIMARY,
          letterSpacing: -1.5,
          textAlign: "center",
        }}
      >
        Distribución de
        <span
          style={{
            background: KEYWORD_GRADIENT,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            marginLeft: 14,
          }}
        >
          clics
        </span>
      </div>

      {/* Bars container */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          gap: 60,
          height: 460,
          padding: "0 40px 60px 40px",
          borderBottom: `2px solid ${TEXT_SECONDARY}`,
          width: "70%",
          justifyContent: "center",
          position: "relative",
        }}
      >
        {/* Horizontal grid lines */}
        {[0.25, 0.5, 0.75].map((p) => (
          <div
            key={p}
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 60 + p * 400,
              height: 1,
              background: "rgba(0,0,0,0.06)",
            }}
          />
        ))}

        {bars.map((b, i) => {
          const start = fallStart + i * 22;
          const local = Math.max(0, frame - start);
          const grow = spring({
            frame: local,
            fps,
            config: { damping: 25, stiffness: 120, mass: 0.8 },
          });
          // Domino tilt
          const tilt = interpolate(local, [0, 30], [-25, 0], {
            extrapolateLeft: "clamp",
            extrapolateRight: "clamp",
          });
          const opacity = interpolate(local, [0, 12], [0, 1], {
            extrapolateLeft: "clamp",
            extrapolateRight: "clamp",
          });
          // Counter
          const pctValue = Math.floor(
            interpolate(local, [10, 40], [0, b.pct], {
              extrapolateLeft: "clamp",
              extrapolateRight: "clamp",
            })
          );
          return (
            <div
              key={b.label}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 14,
                opacity,
              }}
            >
              <div
                style={{
                  fontFamily: fonts.heading,
                  fontWeight: 900,
                  fontSize: 44,
                  color: TEXT_PRIMARY,
                  letterSpacing: -1,
                }}
              >
                {pctValue}%
              </div>
              <div
                style={{
                  width: 110,
                  height: b.height * grow,
                  background: `linear-gradient(180deg, ${b.color} 0%, ${b.color}cc 100%)`,
                  borderRadius: "12px 12px 0 0",
                  transformOrigin: "bottom right",
                  transform: `rotate(${tilt}deg)`,
                  boxShadow: `0 8px 24px ${b.color}33`,
                  position: "relative",
                }}
              >
                {/* Highlight stripe */}
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 8,
                    width: 14,
                    height: "100%",
                    background: "rgba(255,255,255,0.2)",
                    borderRadius: 7,
                  }}
                />
              </div>
              <div
                style={{
                  fontFamily: fonts.heading,
                  fontWeight: 800,
                  fontSize: 32,
                  color: TEXT_SECONDARY,
                  marginTop: 4,
                }}
              >
                {b.label}
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend rows */}
      <div
        style={{
          display: "flex",
          gap: 60,
          marginTop: 12,
        }}
      >
        {[
          { l: "#1 → 27%", c: BLUE_SOLID, d: 4 },
          { l: "#5 → 4%", c: "#2563eb", d: 26 },
          { l: "#10 → 2%", c: "#818cf8", d: 48 },
        ].map((row) => {
          const e = useEnter(140 + row.d);
          return (
            <div
              key={row.l}
              style={{
                opacity: e.opacity,
                transform: `translateY(${e.translateY}px)`,
                display: "flex",
                alignItems: "center",
                gap: 12,
                background: "#ffffff",
                padding: "14px 24px",
                borderRadius: 12,
                boxShadow: CARD_SHADOW,
                fontFamily: fonts.body,
                fontWeight: 700,
                fontSize: 26,
                color: TEXT_PRIMARY,
              }}
            >
              <div
                style={{
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  background: row.c,
                }}
              />
              {row.l}
            </div>
          );
        })}
      </div>
    </AbsoluteFill>
  );
};

// ─────────────────────────────────────────────
// SCENE 5 — REFRAME (typewriter)
// ─────────────────────────────────────────────
const Scene5Reframe: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const line1Full = "No trabaja mejor que tú.";
  const line2Full = "Solo aparece antes.";

  const line1Start = 8;
  const line1End = 70;
  const line2Start = 80;
  const line2End = 150;

  const c1 = Math.max(
    0,
    Math.floor(
      interpolate(frame, [line1Start, line1End], [0, line1Full.length], {
        extrapolateLeft: "clamp",
        extrapolateRight: "clamp",
      })
    )
  );
  const c2 = Math.max(
    0,
    Math.floor(
      interpolate(frame, [line2Start, line2End], [0, line2Full.length], {
        extrapolateLeft: "clamp",
        extrapolateRight: "clamp",
      })
    )
  );

  const t1 = line1Full.slice(0, c1);
  const t2 = line2Full.slice(0, c2);

  const cursorVisible = c1 < line1Full.length || c2 < line2Full.length;
  const cursorOn = Math.floor(frame / 12) % 2 === 0;
  const showCursor1 = c1 < line1Full.length;
  const showCursor2 = c1 >= line1Full.length && c2 <= line2Full.length;

  // Underline "aparece" once typed
  const aparAppearAt = c2 >= "Solo aparece".length;
  const underlineProgress = interpolate(
    frame,
    [line2End - 5, line2End + 25],
    [0, 1],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
  );

  const exit = interpolate(frame, [214, 232], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  return (
    <AbsoluteFill
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        opacity: exit,
        flexDirection: "column",
        gap: 16,
        padding: 60,
        background: "rgba(255,255,255,0.5)",
      }}
    >
      {/* Floating quote marks */}
      <div
        style={{
          position: "absolute",
          left: "10%",
          top: "20%",
          fontFamily: fonts.heading,
          fontSize: 200,
          color: BLUE_SOLID,
          opacity: 0.08,
          fontWeight: 900,
          transform: `translateY(${Math.sin(frame / 30) * 6}px)`,
        }}
      >
        "
      </div>
      <div
        style={{
          position: "absolute",
          right: "10%",
          bottom: "10%",
          fontFamily: fonts.heading,
          fontSize: 200,
          color: BLUE_SOLID,
          opacity: 0.08,
          fontWeight: 900,
          transform: `translateY(${Math.cos(frame / 30) * 6}px) scaleX(-1)`,
        }}
      >
        "
      </div>

      <div
        style={{
          fontFamily: fonts.heading,
          fontWeight: 900,
          fontSize: 80,
          color: TEXT_PRIMARY,
          letterSpacing: -2,
          textAlign: "center",
          minHeight: 95,
        }}
      >
        {t1}
        {showCursor1 && (
          <span
            style={{
              display: "inline-block",
              width: 5,
              height: 70,
              background: TEXT_PRIMARY,
              marginLeft: 6,
              verticalAlign: "middle",
              opacity: cursorOn ? 1 : 0,
            }}
          />
        )}
      </div>

      <div
        style={{
          fontFamily: fonts.heading,
          fontWeight: 900,
          fontSize: 80,
          color: TEXT_PRIMARY,
          letterSpacing: -2,
          textAlign: "center",
          minHeight: 95,
          position: "relative",
          display: "inline-block",
        }}
      >
        {t2.includes("aparece") ? (
          <>
            {t2.split("aparece")[0]}
            <span style={{ position: "relative", display: "inline-block" }}>
              aparece
              <div
                style={{
                  position: "absolute",
                  bottom: -2,
                  left: 0,
                  height: 6,
                  width: `${underlineProgress * 100}%`,
                  background: BLUE_SOLID,
                  borderRadius: 3,
                }}
              />
            </span>
            {t2.split("aparece")[1] || ""}
          </>
        ) : (
          t2
        )}
        {showCursor2 && (
          <span
            style={{
              display: "inline-block",
              width: 5,
              height: 70,
              background: TEXT_PRIMARY,
              marginLeft: 6,
              verticalAlign: "middle",
              opacity: cursorOn ? 1 : 0,
            }}
          />
        )}
      </div>
    </AbsoluteFill>
  );
};

// ─────────────────────────────────────────────
// SCENE 6 — SOLUCIÓN (mobile mockup)
// ─────────────────────────────────────────────
const Scene6Solution: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const phoneEnter = useEnter(4);
  const phoneFloat = Math.sin(frame / 20) * 8;

  // Position list animation: from 8 → 1
  const positions = [1, 2, 3, 4, 5, 6, 7, 8];
  // Highlight slot moves from index 7 (pos 8) to index 0 (pos 1)
  const slotIndex = interpolate(frame, [40, 150], [7, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const labelEnter = useEnter(160);

  const exit = interpolate(frame, [164, 182], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  return (
    <AbsoluteFill
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        opacity: exit,
        gap: 80,
        padding: 80,
      }}
    >
      {/* Rising lines bg */}
      {[0, 1, 2, 3, 4, 5].map((i) => {
        const off = ((frame * 1.5 + i * 60) % 600) - 300;
        return (
          <div
            key={i}
            style={{
              position: "absolute",
              left: `${20 + i * 12}%`,
              bottom: 0,
              width: 2,
              height: 600 + off,
              background: `linear-gradient(180deg, transparent 0%, ${BLUE_SOLID}33 50%, transparent 100%)`,
              transform: `translateY(${off}px)`,
              opacity: 0.4,
            }}
          />
        );
      })}

      {/* Phone mockup */}
      <div
        style={{
          opacity: phoneEnter.opacity,
          transform: `translateY(${phoneEnter.translateY + phoneFloat}px) scale(${phoneEnter.spring})`,
          width: 320,
          height: 640,
          background: "#ffffff",
          borderRadius: 42,
          boxShadow: "0 30px 60px rgba(0,0,0,0.12)",
          border: "8px solid #1a1d1f",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Notch */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: "50%",
            transform: "translateX(-50%)",
            width: 110,
            height: 24,
            background: "#1a1d1f",
            borderRadius: "0 0 14px 14px",
          }}
        />

        {/* Status bar */}
        <div
          style={{
            padding: "32px 24px 16px 24px",
            display: "flex",
            justifyContent: "space-between",
            fontFamily: fonts.body,
            fontSize: 12,
            fontWeight: 700,
            color: TEXT_PRIMARY,
          }}
        >
          <span>9:41</span>
          <span>●●●</span>
        </div>

        {/* Web mockup content */}
        <div style={{ padding: "0 24px" }}>
          <div
            style={{
              height: 14,
              background: BLUE_SOLID,
              borderRadius: 4,
              width: "70%",
              marginBottom: 14,
            }}
          />
          <div
            style={{
              height: 100,
              background: "linear-gradient(135deg, #e7eaee 0%, #f0f2f5 100%)",
              borderRadius: 12,
              marginBottom: 16,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 2L2 12h3v8h6v-6h2v6h6v-8h3L12 2z"
                fill={BLUE_SOLID}
              />
            </svg>
          </div>
          <div style={{ height: 8, background: "#e0e3e7", borderRadius: 4, marginBottom: 8 }} />
          <div style={{ height: 8, background: "#e0e3e7", borderRadius: 4, width: "85%", marginBottom: 8 }} />
          <div style={{ height: 8, background: "#e0e3e7", borderRadius: 4, width: "60%", marginBottom: 16 }} />
          <div
            style={{
              height: 36,
              background: BLUE_SOLID,
              borderRadius: 8,
              marginBottom: 12,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontFamily: fonts.body,
              fontSize: 11,
              fontWeight: 700,
            }}
          >
            CONTACTAR
          </div>
        </div>
      </div>

      {/* Side ranking list */}
      <div
        style={{
          opacity: phoneEnter.opacity,
          transform: `translateY(${phoneEnter.translateY}px)`,
          display: "flex",
          flexDirection: "column",
          gap: 10,
          width: 360,
        }}
      >
        {positions.map((pos, idx) => {
          const isHighlight =
            Math.round(slotIndex) === idx;
          return (
            <div
              key={pos}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 16,
                background: isHighlight ? BLUE_SOLID : "#ffffff",
                color: isHighlight ? "#ffffff" : TEXT_PRIMARY,
                borderRadius: 10,
                padding: "14px 20px",
                boxShadow: isHighlight
                  ? `0 12px 28px ${BLUE_SOLID}55`
                  : CARD_SHADOW,
                fontFamily: fonts.body,
                fontWeight: 700,
                fontSize: 22,
                transform: isHighlight ? "scale(1.05)" : "scale(1)",
                transition: "all 0.2s",
              }}
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  background: isHighlight ? "rgba(255,255,255,0.2)" : "#f0f2f5",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 16,
                  fontWeight: 900,
                }}
              >
                {pos}
              </div>
              <div
                style={{
                  height: 8,
                  flex: 1,
                  background: isHighlight ? "rgba(255,255,255,0.4)" : "#dee1e5",
                  borderRadius: 4,
                }}
              />
            </div>
          );
        })}
      </div>

      {/* Label below */}
      <div
        style={{
          position: "absolute",
          bottom: 80,
          left: 0,
          right: 0,
          textAlign: "center",
          opacity: labelEnter.opacity,
          transform: `translateY(${labelEnter.translateY}px)`,
          fontFamily: fonts.heading,
          fontWeight: 900,
          fontSize: 44,
          color: TEXT_PRIMARY,
          letterSpacing: -1,
        }}
      >
        Web nueva
        <span style={{ color: BLUE_SOLID, margin: "0 14px" }}>+</span>
        <span
          style={{
            background: KEYWORD_GRADIENT,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          SEO local
        </span>
      </div>
    </AbsoluteFill>
  );
};

// ─────────────────────────────────────────────
// SCENE 7 — CIERRE CONCEPTUAL
// ─────────────────────────────────────────────
const Scene7Closing: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const line1 = useEnter(4);
  const line2 = useEnter(26);
  const dotEnter = spring({
    frame: frame - 60,
    fps,
    config: { damping: 25, stiffness: 120, mass: 0.8 },
  });

  const exit = interpolate(frame, [92, 110], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  return (
    <AbsoluteFill
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        opacity: exit,
        flexDirection: "column",
        background: "#ffffff",
        padding: 100,
      }}
    >
      {/* Editorial frame lines */}
      <div
        style={{
          position: "absolute",
          left: 80,
          top: 80,
          right: 80,
          height: 2,
          background: TEXT_PRIMARY,
          opacity: 0.15,
          transform: `scaleX(${line1.spring})`,
          transformOrigin: "left",
        }}
      />
      <div
        style={{
          position: "absolute",
          left: 80,
          bottom: 80,
          right: 80,
          height: 2,
          background: TEXT_PRIMARY,
          opacity: 0.15,
          transform: `scaleX(${line2.spring})`,
          transformOrigin: "right",
        }}
      />

      <div
        style={{
          opacity: line1.opacity,
          transform: `translateY(${line1.translateY}px)`,
          fontFamily: fonts.heading,
          fontWeight: 500,
          fontSize: 78,
          color: TEXT_PRIMARY,
          letterSpacing: -2,
          textAlign: "center",
          fontStyle: "italic",
        }}
      >
        SEO local no es marketing.
      </div>
      <div
        style={{
          opacity: line2.opacity,
          transform: `translateY(${line2.translateY}px)`,
          fontFamily: fonts.heading,
          fontWeight: 900,
          fontSize: 92,
          color: TEXT_PRIMARY,
          letterSpacing: -2.5,
          textAlign: "center",
          marginTop: 8,
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        Es justicia distributiva
        <span
          style={{
            display: "inline-block",
            width: 22,
            height: 22,
            borderRadius: "50%",
            background: "#dc2626",
            marginLeft: 8,
            transform: `scale(${dotEnter})`,
            boxShadow: "0 4px 14px rgba(220,38,38,0.4)",
          }}
        />
      </div>
    </AbsoluteFill>
  );
};

// ─────────────────────────────────────────────
// SCENE 8 — CTA
// ─────────────────────────────────────────────
const Scene8CTA: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const logoEnter = useEnter(4);
  const urlEnter = useEnter(28);

  // Pulse on button
  const pulse = 1 + Math.sin(frame / 8) * 0.04;

  // Cursor blink
  const cursorOn = Math.floor(frame / 12) % 2 === 0;

  // Final fade to black
  const fadeBlack = interpolate(frame, [115, 141], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  return (
    <AbsoluteFill
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        gap: 32,
        padding: 60,
      }}
    >
      {/* Radiating circles */}
      {[0, 1, 2].map((i) => {
        const phase = (frame / 30 + i / 3) % 1;
        const scale = 0.5 + phase * 1.5;
        const op = (1 - phase) * 0.25;
        return (
          <div
            key={i}
            style={{
              position: "absolute",
              left: "50%",
              top: "50%",
              width: 300,
              height: 300,
              marginLeft: -150,
              marginTop: -150,
              borderRadius: "50%",
              border: `2px solid ${BLUE_SOLID}`,
              transform: `scale(${scale})`,
              opacity: op,
            }}
          />
        );
      })}

      {/* Logo */}
      <div
        style={{
          opacity: logoEnter.opacity,
          transform: `translateY(${logoEnter.translateY}px) scale(${pulse})`,
          padding: "28px 56px",
          background: `linear-gradient(135deg, ${BLUE_SOLID} 0%, #2563eb 100%)`,
          borderRadius: 18,
          boxShadow: `0 20px 50px ${BLUE_SOLID}55`,
          display: "flex",
          alignItems: "center",
          gap: 18,
        }}
      >
        <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
          <circle cx="30" cy="30" r="26" stroke="#ffffff" strokeWidth="4" />
          <circle cx="30" cy="30" r="10" fill="#ffffff" />
        </svg>
        <div
          style={{
            fontFamily: fonts.heading,
            fontWeight: 900,
            fontSize: 56,
            color: "#ffffff",
            letterSpacing: -1.5,
          }}
        >
          Core Digital
        </div>
      </div>

      {/* URL */}
      <div
        style={{
          opacity: urlEnter.opacity,
          transform: `translateY(${urlEnter.translateY}px)`,
          fontFamily: "ui-monospace, 'SF Mono', Menlo, monospace",
          fontSize: 40,
          fontWeight: 700,
          color: TEXT_PRIMARY,
          background: "#ffffff",
          padding: "16px 32px",
          borderRadius: 10,
          boxShadow: CARD_SHADOW,
          display: "flex",
          alignItems: "center",
          gap: 6,
          border: `2px solid ${BLUE_SOLID}22`,
        }}
      >
        coredigital.es
        <span
          style={{
            display: "inline-block",
            width: 4,
            height: 36,
            background: TEXT_PRIMARY,
            marginLeft: 4,
            opacity: cursorOn ? 1 : 0,
          }}
        />
      </div>

      {/* Black fade overlay */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: "#000000",
          opacity: fadeBlack,
          pointerEvents: "none",
        }}
      />
    </AbsoluteFill>
  );
};

// ─────────────────────────────────────────────
// MAIN COMPOSITION
// ─────────────────────────────────────────────
export const Reel_cmohmvwuo00043pqvdfg53pmb: React.FC = () => {
  return (
    <AbsoluteFill style={{ background: BG_BASE, fontFamily: fonts.body }}>
      <ContinuousBackground />

      <Sequence from={5}>
        <Audio src={staticFile("audio/cmohmvwuo00043pqvdfg53pmb.mp3")} />
      </Sequence>

      <Sequence from={0} durationInFrames={104} name="Scene 1 - Hook">
        <Scene1Hook />
      </Sequence>

      <Sequence from={104} durationInFrames={132} name="Scene 2 - Puente visual">
        <Scene2Bridge />
      </Sequence>

      <Sequence from={236} durationInFrames={107} name="Scene 3 - Dato frio">
        <Scene3ColdFact />
      </Sequence>

      <Sequence from={343} durationInFrames={484} name="Scene 4 - Grafico de barras">
        <Scene4Bars />
      </Sequence>

      <Sequence from={827} durationInFrames={232} name="Scene 5 - Reframe">
        <Scene5Reframe />
      </Sequence>

      <Sequence from={1059} durationInFrames={182} name="Scene 6 - Solucion">
        <Scene6Solution />
      </Sequence>

      <Sequence from={1241} durationInFrames={110} name="Scene 7 - Cierre conceptual">
        <Scene7Closing />
      </Sequence>

      <Sequence from={1351} durationInFrames={141} name="Scene 8 - CTA">
        <Scene8CTA />
      </Sequence>
    </AbsoluteFill>
  );
};
;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
